/**
 * Cursor snapshot for wallpapers.
 *
 * The wallpaper layer is `pointer-events: none` (os.css, `.os-canvas-host`) so
 * desktop icons, marquee selection and the context menu keep working — a
 * wallpaper is never an event target and cannot read the cursor itself. The
 * host tracks it passively here instead.
 *
 * ONE mutable snapshot, no allocation per event: a wallpaper polls this once
 * per frame, so a subscription firing at input rate would deliver the same
 * value dozens of times for nothing. Every listener is `{ passive: true }` and
 * nothing calls `preventDefault`, so no behaviour above the wallpaper changes.
 */
import type { WallpaperPointer } from "@arclog/module-sdk";
import { clientToLayout } from "./ui-scale";
import { osStore } from "./store";

const snap: WallpaperPointer = {
  x: 0,
  y: 0,
  down: false,
  buttons: 0,
  overDesktop: false,
  inside: false,
};

/**
 * Chrome that sits ABOVE the wallpaper. Anything under the cursor from this
 * list means the user is not interacting with the desktop.
 *
 * These class names are load-bearing and were read off the live CSS/JSX —
 * a stale name here fails silently by making `overDesktop` always true, so
 * the influence would never stop over a window.
 */
const CHROME =
  ".window, .dock, .dock-wrap, .os-widget-host, .os-widget-panel," +
  " .os-desktop-tile, .ctx-menu, .os-modal-scrim, .app-menubar, .wsp";

/** The desktop layer itself — the wallpaper host and the click surface over it. */
const DESKTOP = ".desktop-surface, .os-canvas-host";

function isDesktopTarget(t: EventTarget | null): boolean {
  const el = t as Element | null;
  if (!el || typeof el.closest !== "function") return false;
  // Both halves matter: inside the desktop layer AND not inside chrome. That
  // holds whether windows are nested under .desktop-surface or siblings of it.
  return !!el.closest(DESKTOP) && !el.closest(CHROME);
}

function update(e: PointerEvent): void {
  // clientX/Y are ZOOMED px — UI scale is document.body.style.zoom, and a
  // layout length is scaled again on top. clientToLayout does the division;
  // skipping it makes anything cursor-driven miss by the zoom factor below
  // 100% scale, exactly as it did for charts and the context menu.
  const p = clientToLayout(e.clientX, e.clientY);
  snap.x = p.x;
  snap.y = p.y;
  snap.buttons = e.buttons;
  snap.down = e.buttons !== 0;
  snap.inside = true;
  snap.overDesktop = isDesktopTarget(e.target);
}

let installed = false;

/** Idempotent — the shell may re-run boot on account switch. */
export function installWallpaperPointer(): () => void {
  if (installed) return () => {};
  installed = true;

  const opts = { passive: true } as const;
  const onLeave = () => {
    snap.inside = false;
    snap.down = false;
    snap.buttons = 0;
    snap.overDesktop = false;
  };

  document.addEventListener("pointermove", update, opts);
  document.addEventListener("pointerdown", update, opts);
  document.addEventListener("pointerup", update, opts);
  // A drag that ends outside the window never fires pointerup on us, so a
  // stale `down: true` would stick until the next move.
  document.addEventListener("pointercancel", onLeave, opts);
  document.addEventListener("pointerleave", onLeave, opts);
  window.addEventListener("blur", onLeave, opts);

  return () => {
    document.removeEventListener("pointermove", update);
    document.removeEventListener("pointerdown", update);
    document.removeEventListener("pointerup", update);
    document.removeEventListener("pointercancel", onLeave);
    document.removeEventListener("pointerleave", onLeave);
    window.removeEventListener("blur", onLeave);
    installed = false;
  };
}

export function getWallpaperPointer(): WallpaperPointer {
  // Frozen while paused (hidden tab, reduced motion) so a flock does not
  // resume mid-gesture against a cursor that moved while nobody was looking.
  if (osStore.wallpaperPaused || document.hidden) {
    return { ...snap, down: false, buttons: 0 };
  }
  return snap;
}
